import { ApiBase } from '@bymason/api-base';
import { getEndpointTemplate } from '@bymason/utils';
import {
  ArtifactModel,
  ConfigArtifact,
  CreateArtifactDeploymentOptions,
  CreateArtifactDeploymentResult,
  CreateArtifactOptions,
  GetArtifactsOptions,
  GetArtifactsOptionsList,
  GetArtifactsResult,
  GetConfigArtifactOptions,
} from './types';

export * from './types';

class Artifacts extends ApiBase {
  private static instance: Artifacts;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns An Artifacts instance
   */
  public static getInstance(): Artifacts {
    if (!Artifacts.instance) {
      Artifacts.instance = new Artifacts();
      Artifacts.instance.configure({
        legacy: true,
      });
    }

    return Artifacts.instance;
  }

  /**
   * Gets artifacts list.
   * @param options metadata required to get artifacts list.
   * @param clientToken token required for proper authorization.
   * @returns either void or a pageable structure that includes artifacts.
   */
  public async getArtifacts(options: GetArtifactsOptions, clientToken: string) {
    const { type, name, version } = options;
    const pathTemplate = `registry/artifacts/:customerId${type ? '/:type' : ''}${
      type && name ? '/:name' : ''
    }${type && name && version ? '/:version' : ''}`;
    const urlTemplate = this.getEndpointTemplate(pathTemplate);
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<GetArtifactsResult>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets project config artifact version.
   * @param options metadata required to get config.
   * @param clientToken token required for proper authorization.
   * @returns either void or a pageable structure that includes artifacts.
   */
  public async getConfigArtifact(options: GetConfigArtifactOptions, clientToken: string) {
    const pathTemplate = `registry/artifacts/:customerId/config/:name/:version`;
    const urlTemplate = this.getEndpointTemplate(pathTemplate);
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<ConfigArtifact>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets an artifact given name & version
   * One api call for each item in optionsList param.
   * @param options  metadata required to get artifact.
   * @param clientToken token required for proper authorization.
   * @returns either void or an array of artifacts.
   */
  public async getArtifactByVersion(options: GetArtifactsOptions, clientToken: string) {
    const pathTemplate = `registry/artifacts/:customerId/:type/:name/:version`;
    const urlTemplate = this.getEndpointTemplate(pathTemplate);
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<ArtifactModel>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets list of artifact versions via Promise.all,
   * One api call for each item in optionsList param.
   * @param optionList array of metadata required to get artifacts.
   * @param clientToken token required for proper authorization.
   * @returns either void or an array of artifacts.
   */
  public async getArtifactsByVersionList(optionsList: GetArtifactsOptionsList, clientToken: string) {
    const pathTemplate = `registry/artifacts/:customerId/:type/:name/:version`;
    const urlTemplate = this.getEndpointTemplate(pathTemplate);
    const urls = optionsList.map((options) => this.expandUrlParams(urlTemplate, options));

    const dataResponse = await Promise.all(
      urls.map((url) => {
        return this.callJsonEndpoint<ArtifactModel>({
          clientToken,
          errorOn404: false,
          method: 'GET',
          url,
        });
      })
    );

    return dataResponse;
  }

  /**
   * Creates an artifact deployment.
   * @param options model data required to deploy an artifact.
   * @param clientToken token required for proper authorization.
   * @returns object or void
   */
  public async createArtifactDeployment(options: CreateArtifactDeploymentOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('deploy');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<CreateArtifactDeploymentResult>({
      body: JSON.stringify(options),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }

  /**
   * Uploads a file as a new artifact.
   * @param options model data required to create an artifact.
   * @param clientToken token required for proper authorization.
   * @returns object or void
   */
  public async createArtifact(options: CreateArtifactOptions, clientToken: string) {
    // Unlike the rest of the endpoints above, this is not a legacy endpoint
    // This is part of the new artifacts service.
    const urlTemplate = getEndpointTemplate('/artifacts/upload?type=:type', 'v1', 'default');
    const url = this.expandUrlParams(urlTemplate, options);
    const file = new File([options.file], options.fileName);
    const body = new FormData();

    body.append('artifact', file, options.fileName);

    const dataResponse = await this.callFormEndpoint<ArtifactModel>({
      body,
      clientToken,
      method: 'POST',
      url,
      cacheKey: `${url}-${options.fileName}`,
    });

    return dataResponse;
  }

  /**
   * Uploads a file as a new artifact.
   * @param options model data required to create an artifact.
   * @param clientToken token required for proper authorization.
   * @returns object or void
   */
  public async createArtifactXMLHTTP(
    options: CreateArtifactOptions,
    clientToken: string,
    onProgress: (progress: number) => void
  ) {
    // Unlike the rest of the endpoints above, this is not a legacy endpoint
    // This is part of the new artifacts service.
    const urlTemplate = getEndpointTemplate('/artifacts/upload?type=:type', 'v1', 'default');
    const url = this.expandUrlParams(urlTemplate, options);
    const file = new File([options.file], options.fileName);
    const body = new FormData();

    body.append('artifact', file, options.fileName);

    const { promise, abort } = await this.uploadFile<ArtifactModel>({
      body,
      clientToken,
      method: 'POST',
      url,
      onProgress: onProgress,
    });

    return { promise, abort };
  }
}

export const artifactsClient = Artifacts.getInstance();
